import mixpanel from 'mixpanel-browser';
import { appConfig } from '../data/AppConfig';

const MIXPANEL_TOKEN = appConfig.REACT_APP_MIXPANEL_TOKEN ?? '';
const isMixpanelEnabled = Boolean(MIXPANEL_TOKEN);

if (isMixpanelEnabled) {
    mixpanel.init(MIXPANEL_TOKEN, {
        autocapture: false,
        record_sessions_percent: 0,
    });
} else {
    console.warn('Mixpanel token is missing! Tracking is disabled.');
}

const track = (eventName: string, properties?: Record<string, unknown>) => {
    if (!isMixpanelEnabled) {
        return;
    }

    try {
        mixpanel.track(eventName, properties);
    } catch (error) {
        console.warn('Mixpanel track failed; skipping event.', error);
    }
};

/**
 * Ties the current session to a known user and stamps their profile. Safe to
 * call on every /me resolution — Mixpanel dedupes by distinct id.
 */
const identifyUser = (
    id: string,
    profile?: Record<string, unknown>,
) => {
    if (!isMixpanelEnabled) {
        return;
    }

    try {
        mixpanel.identify(id);
        if (profile) {
            mixpanel.people.set(profile);
        }
    } catch (error) {
        console.warn('Mixpanel identify failed; skipping.', error);
    }
};

/**
 * Registers super properties — stamped onto every subsequent event until
 * reset. Use for slow-moving session context (org, role) so individual
 * track() calls stay lean.
 */
const registerSuperProperties = (properties: Record<string, unknown>) => {
    if (!isMixpanelEnabled) {
        return;
    }

    try {
        mixpanel.register(properties);
    } catch (error) {
        console.warn('Mixpanel register failed; skipping.', error);
    }
};

/**
 * Clears the identified user and any registered super properties. Call on
 * logout so a shared browser does not bleed one user's identity into the next.
 */
const resetIdentity = () => {
    if (!isMixpanelEnabled) {
        return;
    }

    try {
        mixpanel.reset();
    } catch (error) {
        console.warn('Mixpanel reset failed; skipping.', error);
    }
};

export {
    mixpanel,
    track,
    identifyUser,
    registerSuperProperties,
    resetIdentity,
    isMixpanelEnabled,
};
